import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Zap,
  Wallet,
  Shield,
  Smartphone,
  TrendingUp,
  Users,
  CheckCircle,
  Clock,
  FileText,
  CreditCard,
  Award,
  Star,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { UserType } from '../../types';
import { LoanCalculator } from '../../components/LoanCalculator';
import { Button } from '../../components/Button';

export const Home: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-cta-badge">
            <Button
              variant="primary"
              size="md"
              icon={<Zap size={18} />}
              onClick={() => navigate(isAuthenticated && user?.user_type === UserType.CUSTOMER ? '/customer/applications/new' : '/register')}
            >
              {isAuthenticated && user?.user_type === UserType.CUSTOMER ? 'Apply for Loan Now' : 'Start Your Application'}
            </Button>
          </div>
          <h1>Get the Funding You Need, When You Need It</h1>
          <p className="hero-subtitle">
            Access quick, affordable loans from ₦50,000 to ₦10,000,000. Whether you're growing your business,
            handling emergencies, or seizing opportunities - we've got you covered with flexible terms and
            transparent rates.
          </p>
          <div className="hero-stats">
            <div className="hero-stat">
              <div className="hero-stat-value">24hrs</div>
              <div className="hero-stat-label">Approval Time</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-value">15,000+</div>
              <div className="hero-stat-label">Happy Customers</div>
            </div>
            <div className="hero-stat">
              <div className="hero-stat-value">₦5B+</div>
              <div className="hero-stat-label">Disbursed</div>
            </div>
          </div>
          <div className="hero-actions">
            {isAuthenticated ? (
              user?.user_type === UserType.CUSTOMER ? (
                <>
                  <Button
                    variant="primary"
                    size="lg"
                    icon={<Wallet size={20} />}
                    onClick={() => navigate('/customer/dashboard')}
                  >
                    View Dashboard
                  </Button>
                  <Button
                    variant="secondary"
                    size="lg"
                    icon={<FileText size={20} />}
                    onClick={() => navigate('/customer/applications/new')}
                  >
                    Apply Now
                  </Button>
                </>
              ) : (
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => navigate('/admin/dashboard')}
                >
                  Admin Dashboard
                </Button>
              )
            ) : (
              <>
                <Button
                  variant="primary"
                  size="lg"
                  icon={<Zap size={20} />}
                  onClick={() => navigate('/register')}
                >
                  Get Started Free
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => navigate('/loan-products')}
                >
                  Explore Loan Options
                </Button>
              </>
            )}
          </div>
          <p className="hero-note">
            <CheckCircle size={16} /> No collateral required • <CheckCircle size={16} /> Apply in 5 minutes • <CheckCircle size={16} /> Money in 24 hours
          </p>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section">
        <div className="section-container">
          <h2>Tired of Traditional Bank Hassles?</h2>
          <p className="section-subtitle">We understand the frustrations of getting a loan in Nigeria</p>
          <div className="problems-grid">
            <div className="problem-card">
              <h3>❌ Endless Paperwork</h3>
              <p>Banks require mountains of documents and make you visit multiple times</p>
            </div>
            <div className="problem-card">
              <h3>❌ Weeks of Waiting</h3>
              <p>Traditional lenders take 2-4 weeks to approve loans, losing you opportunities</p>
            </div>
            <div className="problem-card">
              <h3>❌ Hidden Charges</h3>
              <p>Unclear fees, processing charges, and surprise deductions eat into your loan</p>
            </div>
            <div className="problem-card">
              <h3>❌ Collateral Requirements</h3>
              <p>Banks demand property or assets you may not have</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="solution-section">
        <div className="section-container">
          <h2>Introducing a Better Way to Borrow</h2>
          <p className="section-subtitle">Coin MFI removes all the friction from getting a loan</p>
          <div className="solution-grid">
            <div className="solution-card">
              <div className="solution-icon">
                <Smartphone size={40} />
              </div>
              <h3>100% Digital Application</h3>
              <p>Apply from your phone or computer in just 5 minutes. No branch visits, no paperwork hassles.</p>
            </div>
            <div className="solution-card">
              <div className="solution-icon">
                <Clock size={40} />
              </div>
              <h3>Instant Decisions</h3>
              <p>Get approved within 24 hours. Our smart system evaluates your application automatically.</p>
            </div>
            <div className="solution-card">
              <div className="solution-icon">
                <Shield size={40} />
              </div>
              <h3>No Collateral Needed</h3>
              <p>Access loans up to ₦10M without property or asset requirements. Your business is enough.</p>
            </div>
            <div className="solution-card">
              <div className="solution-icon">
                <TrendingUp size={40} />
              </div>
              <h3>Clear, Fair Pricing</h3>
              <p>Know exactly what you're paying. No hidden fees, no surprises - just honest lending.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <div className="section-container">
          <h2>Get Your Loan in 3 Simple Steps</h2>
          <p className="section-subtitle">From application to disbursement in as fast as 24 hours</p>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-icon">
                <FileText size={32} />
              </div>
              <h3>Apply Online</h3>
              <p>Fill out our simple online form in just 5 minutes. Provide basic information about yourself and your loan needs.</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-icon">
                <CheckCircle size={32} />
              </div>
              <h3>Get Approved</h3>
              <p>Our automated system reviews your application instantly. Get a decision within 24 hours, often much faster.</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-icon">
                <CreditCard size={32} />
              </div>
              <h3>Receive Funds</h3>
              <p>Once approved, funds are transferred directly to your bank account. Start using your loan immediately!</p>
            </div>
          </div>
        </div>
      </section>

      {/* Loan Calculator Section */}
      <LoanCalculator />

      {/* Testimonials Section */}
      <section className="testimonials">
        <div className="section-container">
          <h2>What Our Customers Say</h2>
          <p className="section-subtitle">Join thousands of satisfied customers across Nigeria</p>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-stars">
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
              </div>
              <p className="testimonial-text">
                "I needed capital to expand my textile business in Onitsha. Coin MFI approved my ₦2M loan in just one day!
                The process was incredibly smooth and the support team was very helpful."
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">CN</div>
                <div>
                  <div className="author-name">Chioma Nwosu</div>
                  <div className="author-title">Textile Business Owner, Onitsha</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
              </div>
              <p className="testimonial-text">
                "As a small restaurant owner, getting loans from traditional banks was impossible. Coin MFI gave me ₦500K
                to renovate my kitchen. No collateral, no stress. Highly recommended!"
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">AA</div>
                <div>
                  <div className="author-name">Abubakar Aliyu</div>
                  <div className="author-title">Restaurant Owner, Kano</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
              </div>
              <p className="testimonial-text">
                "I was skeptical about online loans, but Coin MFI proved me wrong. Got ₦750K for my logistics business within
                24 hours. The interest rates are fair and transparent. Best decision ever!"
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">OE</div>
                <div>
                  <div className="author-name">Oluwaseun Eze</div>
                  <div className="author-title">Logistics Company, Lagos</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
              </div>
              <p className="testimonial-text">
                "Emergency medical expenses came up and I needed money fast. Coin MFI approved ₦300K in hours.
                The entire process was digital and stress-free. Thank you!"
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">FM</div>
                <div>
                  <div className="author-name">Folake Martins</div>
                  <div className="author-title">Civil Servant, Ibadan</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
              </div>
              <p className="testimonial-text">
                "I've taken 3 loans from Coin MFI for my poultry farm. Each time the process gets easier.
                Their customer service is top-notch and they really understand small business needs."
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">EB</div>
                <div>
                  <div className="author-name">Emmanuel Bassey</div>
                  <div className="author-title">Poultry Farmer, Port Harcourt</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
                <Star size={20} fill="#F59E0B" color="#F59E0B" />
              </div>
              <p className="testimonial-text">
                "Being a woman entrepreneur, I faced many challenges getting bank loans. Coin MFI believed in my fashion
                business and gave me ₦1.5M. Today, I have 3 outlets. Forever grateful!"
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">AM</div>
                <div>
                  <div className="author-name">Amina Mohammed</div>
                  <div className="author-title">Fashion Designer, Abuja</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="trust-section">
        <div className="section-container">
          <h2>Why Thousands Trust Coin MFI</h2>
          <div className="trust-grid">
            <div className="trust-card">
              <div className="trust-icon">
                <Shield size={32} />
              </div>
              <h3>Licensed & Regulated</h3>
              <p>Fully licensed by CBN and regulated by Nigerian authorities</p>
            </div>
            <div className="trust-card">
              <div className="trust-icon">
                <Award size={32} />
              </div>
              <h3>Award Winning</h3>
              <p>Recognized for excellence in digital financial services</p>
            </div>
            <div className="trust-card">
              <div className="trust-icon">
                <Users size={32} />
              </div>
              <h3>15,000+ Happy Customers</h3>
              <p>Join thousands of Nigerians who trust us with their financing</p>
            </div>
            <div className="trust-card">
              <div className="trust-icon">
                <TrendingUp size={32} />
              </div>
              <h3>₦5 Billion+ Disbursed</h3>
              <p>Helping Nigerian businesses and individuals grow since 2020</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="section-container">
          <h2>Frequently Asked Questions</h2>
          <p className="section-subtitle">Everything you need to know about our loans</p>
          <div className="faq-list">
            <div className="faq-item">
              <button className="faq-question" onClick={() => toggleFaq(0)}>
                <span>How quickly can I get my loan approved?</span>
                {openFaq === 0 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {openFaq === 0 && (
                <div className="faq-answer">
                  Most applications are reviewed within 24 hours. Once approved, funds are typically disbursed
                  to your bank account within the same day. In some cases, you can get your money in as little as 2-4 hours!
                </div>
              )}
            </div>

            <div className="faq-item">
              <button className="faq-question" onClick={() => toggleFaq(1)}>
                <span>Do I need collateral to get a loan?</span>
                {openFaq === 1 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {openFaq === 1 && (
                <div className="faq-answer">
                  No! We offer unsecured loans up to ₦10,000,000. We evaluate your application based on your
                  income, business performance, and credit history - not on property or assets you own.
                </div>
              )}
            </div>

            <div className="faq-item">
              <button className="faq-question" onClick={() => toggleFaq(2)}>
                <span>What documents do I need to apply?</span>
                {openFaq === 2 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {openFaq === 2 && (
                <div className="faq-answer">
                  You'll need a valid ID (National ID, Voter's Card, or Driver's License), proof of income
                  (bank statements or business documents), and utility bill for address verification. Everything
                  can be uploaded digitally during your application.
                </div>
              )}
            </div>

            <div className="faq-item">
              <button className="faq-question" onClick={() => toggleFaq(3)}>
                <span>What are your interest rates?</span>
                {openFaq === 3 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {openFaq === 3 && (
                <div className="faq-answer">
                  Our interest rates range from 2% to 5% per month depending on the loan amount, duration, and
                  your credit profile. We provide a complete breakdown before you accept any loan - no hidden charges!
                </div>
              )}
            </div>

            <div className="faq-item">
              <button className="faq-question" onClick={() => toggleFaq(4)}>
                <span>Can I repay my loan early?</span>
                {openFaq === 4 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {openFaq === 4 && (
                <div className="faq-answer">
                  Absolutely! We encourage early repayment. You can settle your loan anytime and save on interest.
                  There are no prepayment penalties - we actually reward responsible borrowers with better terms on future loans.
                </div>
              )}
            </div>

            <div className="faq-item">
              <button className="faq-question" onClick={() => toggleFaq(5)}>
                <span>What if I have poor credit history?</span>
                {openFaq === 5 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {openFaq === 5 && (
                <div className="faq-answer">
                  We look at more than just credit scores. If you have a steady income or viable business, we may
                  still approve your loan. Start with a smaller amount, build your repayment history with us, and
                  qualify for larger loans over time.
                </div>
              )}
            </div>

            <div className="faq-item">
              <button className="faq-question" onClick={() => toggleFaq(6)}>
                <span>How do I make repayments?</span>
                {openFaq === 6 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {openFaq === 6 && (
                <div className="faq-answer">
                  Repayments are easy! Pay online through our platform using your debit card, bank transfer, or USSD.
                  You can also set up automatic deductions from your bank account. We'll send you reminders before each
                  payment is due.
                </div>
              )}
            </div>

            <div className="faq-item">
              <button className="faq-question" onClick={() => toggleFaq(7)}>
                <span>Is my personal information safe?</span>
                {openFaq === 7 ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
              {openFaq === 7 && (
                <div className="faq-answer">
                  Your security is our top priority. We use bank-level 256-bit SSL encryption to protect your data.
                  All information is stored securely and we never share your personal details with third parties without
                  your consent.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="cta">
        <div className="cta-content">
          <h2>Ready to Transform Your Financial Future?</h2>
          <p>Join 15,000+ Nigerians who have achieved their goals with Coin MFI</p>
          <div className="cta-benefits">
            <div><CheckCircle size={20} /> Fast 24-hour approval</div>
            <div><CheckCircle size={20} /> No collateral required</div>
            <div><CheckCircle size={20} /> Flexible repayment terms</div>
            <div><CheckCircle size={20} /> Transparent, fair pricing</div>
          </div>
          {!isAuthenticated ? (
            <Button
              variant="primary"
              size="lg"
              icon={<Zap size={24} />}
              onClick={() => navigate('/register')}
              className="btn-cta"
            >
              Get Your Loan Today
            </Button>
          ) : (
            user?.user_type === UserType.CUSTOMER && (
              <Button
                variant="primary"
                size="lg"
                icon={<FileText size={24} />}
                onClick={() => navigate('/customer/applications/new')}
                className="btn-cta"
              >
                Apply for Loan Now
              </Button>
            )
          )}
          <p className="cta-note">Takes only 5 minutes • No credit card required</p>
        </div>
      </section>

      <style>{`
        /* Tablet Responsive Styles */
        @media (max-width: 1024px) {
          .hero-stats {
            grid-template-columns: repeat(2, 1fr);
            gap: 1.5rem;
          }

          .hero-actions {
            flex-direction: column;
            width: 100%;
          }

          .hero-actions button {
            width: 100%;
          }

          .hero-note {
            flex-direction: column;
            gap: 0.5rem;
            text-align: center;
          }

          .problems-grid,
          .solution-grid,
          .trust-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .steps-grid {
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          .testimonials-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .faq-list {
            padding: 0 1rem;
          }

          .cta-benefits {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .hero {
            padding: 3rem 1.5rem;
            min-height: auto;
          }

          .hero h1 {
            font-size: 2rem;
            line-height: 1.2;
          }

          .hero-subtitle {
            font-size: 1rem;
          }

          .hero-stats {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .hero-stat {
            padding: 1.5rem;
          }

          .hero-stat-value {
            font-size: 2rem;
          }

          .hero-actions {
            flex-direction: column;
            width: 100%;
          }

          .hero-actions button {
            width: 100%;
            justify-content: center;
          }

          .hero-note {
            flex-direction: column;
            gap: 0.5rem;
            text-align: center;
            font-size: 0.875rem;
          }

          .section-container {
            padding: 2rem 1.5rem;
          }

          .section-container h2 {
            font-size: 1.75rem;
          }

          .section-subtitle {
            font-size: 1rem;
          }

          .problems-grid,
          .solution-grid,
          .trust-grid,
          .cta-benefits {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .problem-card,
          .solution-card,
          .trust-card {
            padding: 1.5rem;
          }

          .solution-icon {
            margin-bottom: 1rem;
          }

          .steps-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }

          .step-card {
            padding: 1.5rem;
          }

          .step-number {
            width: 3rem;
            height: 3rem;
            font-size: 1.25rem;
          }

          .testimonials-grid {
            grid-template-columns: 1fr;
          }

          .testimonial-card {
            padding: 1.5rem;
          }

          .faq-list {
            padding: 0;
          }

          .faq-question {
            padding: 1rem;
            font-size: 1rem;
          }

          .faq-answer {
            padding: 1rem;
            font-size: 0.9375rem;
          }

          .cta {
            padding: 3rem 1.5rem;
          }

          .cta h2 {
            font-size: 1.75rem;
          }

          .cta-content p {
            font-size: 1rem;
          }

          .cta-benefits {
            grid-template-columns: 1fr;
            gap: 0.75rem;
            font-size: 0.9375rem;
          }

          .btn-cta {
            width: 100%;
            justify-content: center;
          }

          .cta-note {
            font-size: 0.875rem;
          }
        }

        /* Small Mobile Responsive Styles */
        @media (max-width: 480px) {
          .hero {
            padding: 2rem 1rem;
          }

          .hero h1 {
            font-size: 1.75rem;
          }

          .hero-cta-badge {
            margin-bottom: 1rem;
          }

          .hero-cta-badge button {
            font-size: 0.875rem;
            padding: 0.75rem 1.5rem;
          }

          .section-container {
            padding: 2rem 1rem;
          }

          .section-container h2 {
            font-size: 1.5rem;
          }

          .problem-card h3,
          .solution-card h3,
          .trust-card h3 {
            font-size: 1.125rem;
          }

          .step-card h3 {
            font-size: 1.125rem;
          }

          .testimonial-card {
            padding: 1.25rem;
          }

          .author-avatar {
            width: 2.5rem;
            height: 2.5rem;
            font-size: 0.875rem;
          }

          .cta h2 {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};
